import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { Switch } from "./ui/switch";
import { List, ampm, istDT } from "./PartnerBriefCard";

const CO = { DEL: "Delhi (FBD)", MUM: "Mumbai" };
const pctTxt = (v) => (v == null ? "Nothing due" : `${v}%`);
const dm = (s) => new Date(`${s}T00:00:00`).toLocaleDateString("en-IN", { day: "numeric", month: "short" });

const Tile = ({ label, pct, ok, of, last, tid }) => (
  <div className="border p-3" data-testid={tid}>
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className="text-xl font-semibold">{pctTxt(pct)}</div>
    <div className="text-xs text-muted-foreground">{ok} of {of} · last week {pctTxt(last)}</div>
  </div>
);

const WrapPreview = ({ b }) => (
  <div className="border p-3 mt-3" data-testid="partner-wrap-preview">
    <p className="text-sm font-medium mb-1" data-testid="partner-wrap-line">{b.line}</p>
    <p className="text-xs text-muted-foreground mb-3" data-testid="partner-wrap-week">{b.company_label} · week {dm(b.week_start)} to {dm(b.week_end)}</p>
    {b.all_clear ? <p className="text-sm" data-testid="partner-wrap-all-clear">{b.all_clear}</p> : (<>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
        <Tile tid="partner-wrap-steps" label="Steps on time" pct={b.score.steps_pct} ok={b.score.steps_on_time} of={b.score.steps_measured} last={b.last.steps_pct} />
        <Tile tid="partner-wrap-deliveries" label="Deliveries on time" pct={b.score.deliveries_pct} ok={b.score.deliveries_on_time} of={b.score.deliveries_measured} last={b.last.deliveries_pct} />
      </div>
      <List title="Step owners" tid="partner-wrap-owners" rows={b.owners} render={(o) => `${o.name} · ${o.on_time} of ${o.measured} on time · ${o.pct}%`} />
      <List title="Dispatched this week" tid="partner-wrap-dispatched" rows={b.dispatched} render={(x) => `${x.uid} · ${x.client} · ${x.what}`} />
      <List title="Planned for next week" tid="partner-wrap-next-week" rows={b.next_week} render={(x) => `${x.uid} · ${x.client} · ${x.what}`} />
      <List title="Still late" tid="partner-wrap-late" rows={b.late} render={(x) => `${x.uid} · ${x.client} · ${x.step} · ${x.owner} · ${x.days_late} day(s) late`} />
    </>)}
  </div>
);

const Recipients = ({ d, f, setF }) => {
  const m = d.recipients.MUM;
  return (
    <div className="text-sm mb-3 space-y-2" data-testid="partner-wrap-recipient">
      <div>Mumbai wrap goes to {m ? `${m.name} (${m.email})` : "dhaval@axiomviscom.in"}{m && m.status !== "active" ? " (account switched off, so nothing is sent)" : ""}.</div>
      <label className="flex flex-wrap items-center gap-2">Delhi (FBD) wrap goes to
        <select data-testid="partner-wrap-delhi-person" value={f.delhi_user_id || ""} onChange={(e) => setF({ ...f, delhi_user_id: e.target.value || null })} className="bg-muted/60 border h-9 px-2 text-sm max-w-full">
          <option value="">No one yet (Delhi wrap not sent)</option>{d.people.map((u) => <option key={u.user_id} value={u.user_id}>{u.name} ({u.email})</option>)}</select></label>
    </div>
  );
};

const History = ({ rows }) => (
  <div className="mt-3 text-xs text-muted-foreground" data-testid="partner-wrap-history">
    Last sent: {rows.map((h) => <div key={`${h.date}-${h.company}`}>{CO[h.company] || h.company} · {istDT(h.sent_at || h.at)} · {h.sent_at ? `${h.to_name || h.to}: ${h.summary}` : `Not sent: ${h.skipped}`}{h.sent_at && !h.email_ok ? " · email failed, app alert sent" : ""}</div>)}
  </div>
);

export const PartnerWrapCard = () => {
  const [d, setD] = useState(null);
  const [f, setF] = useState(null);
  const [pv, setPv] = useState(null);
  const load = useCallback(() => api.get("/partner-wrap").then((r) => { setD(r.data); setF(r.data.settings); }).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  if (!d || !f) return null;
  const save = () => api.put("/partner-wrap", { enabled: f.enabled, time: f.time, delhi_user_id: f.delhi_user_id || null })
    .then(() => { toast.success(f.enabled ? `Weekly wraps set for Saturdays at ${ampm(f.time)}` : "Weekly wraps stopped"); load(); }).catch((e) => toast.error(errMsg(e)));
  const preview = (co) => api.get("/partner-wrap/preview", { params: { company: co } }).then((r) => setPv(r.data)).catch((e) => toast.error(errMsg(e)));
  const s = d.settings;
  const changed = f.enabled !== s.enabled || f.time !== s.time || (f.delhi_user_id || null) !== (s.delhi_user_id || null);
  return (
    <div className="ax-panel p-4 mb-4" data-testid="partner-wrap-card">
      <div className="ax-label mb-1">Weekly wraps</div>
      <p className="text-sm text-muted-foreground mb-2">Every Saturday: on-time score, each step owner's score, this week's dispatches, next week's plan and steps still late, by email and app alert. One time and one switch for both.</p>
      <Recipients d={d} f={f} setF={setF} />
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-sm"><Switch data-testid="partner-wrap-enabled" checked={f.enabled} onCheckedChange={(v) => setF({ ...f, enabled: v })} />{f.enabled ? "On" : "Stopped"}</label>
        <label className="flex items-center gap-2 text-sm">Saturdays at<select data-testid="partner-wrap-time" value={f.time} disabled={!f.enabled} onChange={(e) => setF({ ...f, time: e.target.value })} className="bg-muted/60 border h-9 px-2 text-sm">
          {d.times.map((t) => <option key={t} value={t}>{ampm(t)}</option>)}</select></label>
        <button data-testid="partner-wrap-save" disabled={!changed} onClick={save} className="border border-primary text-primary h-9 px-4 text-sm disabled:opacity-40">Save</button>
        <button data-testid="partner-wrap-preview-button" onClick={() => preview("MUM")} className="border h-9 px-4 text-sm">See Mumbai wrap</button>
        <button data-testid="partner-wrap-preview-delhi-button" onClick={() => preview("DEL")} className="border h-9 px-4 text-sm">See Delhi (FBD) wrap</button>
      </div>
      {pv && <WrapPreview b={pv} />}
      {d.history.length > 0 && <History rows={d.history} />}
    </div>
  );
};
